import React, { useEffect, useState, useCallback } from "react";
import { authenticatedGet, authenticatedPost } from "../../api/authenticatedFetch";
import { humanEUR } from "./format";

const BASE = "/api/v1/forecast-predictability/anomalies";

// RecentAnomaliesPanel fetches band-breach anomalies and lets the user dismiss
// (acknowledge) each one via the POST /anomalies/{day}/ack endpoint.
const RecentAnomaliesPanel = () => {
  const [anomalies, setAnomalies] = useState(null);
  const [unacknowledged, setUnacknowledged] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [dismissing, setDismissing] = useState({});

  const load = useCallback(async () => {
    setIsLoading(true);
    setIsError(false);
    try {
      const res = await authenticatedGet(BASE);
      setAnomalies(res.anomalies || []);
      setUnacknowledged(res.unacknowledged || 0);
    } catch (_) {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleDismiss = useCallback(
    async (day) => {
      setDismissing((prev) => ({ ...prev, [day]: true }));
      try {
        await authenticatedPost(`${BASE}/${day}/ack`, {});
        await load();
      } finally {
        setDismissing((prev) => ({ ...prev, [day]: false }));
      }
    },
    [load]
  );

  // ---- render helpers ----

  if (isLoading) {
    return (
      <div className="space-y-2">
        <div className="h-8 animate-pulse rounded-card bg-surface-2" />
        <div className="h-8 animate-pulse rounded-card bg-surface-2" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-card border border-danger bg-danger-soft px-4 py-3 text-sm text-danger">
        Could not load anomalies. Please try again shortly.
      </div>
    );
  }

  // Sort newest-first (day strings are ISO dates so lexicographic sort works).
  const sorted = [...(anomalies || [])].sort((a, b) => b.day.localeCompare(a.day));
  // Dim the whole card when there's nothing to show, matching the Financial Control dashboard's
  // muted empty-state look (a calm "nothing here" rather than a prominent card).
  const dimmed = sorted.length === 0 ? " opacity-60" : "";

  return (
    <div className={`rounded-card border border-card-bd bg-card px-5 py-4 text-sm${dimmed}`}>
      {/* Header */}
      <div className="mb-3 flex items-center gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-text-3">
          Recent anomalies
        </span>
        {unacknowledged > 0 && (
          <span className="inline-flex items-center rounded-full bg-danger-soft px-2 py-0.5 text-xs font-semibold text-danger select-none">
            {unacknowledged}
          </span>
        )}
      </div>

      {/* Empty state */}
      {sorted.length === 0 && (
        <p className="text-xs text-text-3">No recent anomalies.</p>
      )}

      {/* Anomaly rows */}
      {sorted.length > 0 && (
        <ul className="space-y-2">
          {sorted.map((a) => {
            const dirLabel =
              a.direction === "over" ? "above the forecast band" : "below the forecast band";
            const excPct = Math.round((a.rel_exceedance || 0) * 100);
            const isOver = a.direction === "over";

            const dev = a.top_source_deviation;
            const devStr = (dev >= 0 ? "+" : "-") + humanEUR(Math.abs(dev));

            return (
              <li
                key={a.day}
                className={`flex items-start justify-between gap-3 rounded-ctrl border px-4 py-3 ${
                  a.acknowledged
                    ? "border-card-bd opacity-50"
                    : isOver
                    ? "border-danger bg-danger-soft"
                    : "border-card-bd bg-surface-2"
                }`}
              >
                <div className="min-w-0 flex-1">
                  <p className="font-tabular text-text">
                    {a.day}:{" "}
                    <span className="font-semibold">
                      spend {humanEUR(a.realized)}
                    </span>{" "}
                    &mdash;{" "}
                    <span className={isOver ? "text-danger" : "text-text-2"}>
                      {excPct}% {dirLabel}
                    </span>
                    , driven by{" "}
                    <span className="font-medium text-text">{a.top_source}</span>{" "}
                    ({devStr})
                  </p>
                </div>
                {!a.acknowledged && (
                  <button
                    type="button"
                    disabled={dismissing[a.day]}
                    onClick={() => handleDismiss(a.day)}
                    className="shrink-0 rounded-ctrl border border-border px-2 py-0.5 text-xs text-text-2 hover:text-text disabled:opacity-50"
                  >
                    {dismissing[a.day] ? "Dismissing…" : "Dismiss"}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default RecentAnomaliesPanel;
